import { useMemo } from "react"

import { Trans, useI18next } from "gatsby-plugin-react-i18next"
import styled from "styled-components"

import InnerWrapper from "components/InnerWrapper"
import Callout from "components/TextElements/Callout"
import ToolTip from "components/ToolTip"
import colors from "styles/colors"
import media from "styles/media"
import text, { transparentText } from "styles/text"
import UniversalLink from "utils/Loader/UniversalLink"
import useMedia from "utils/useMedia"

type Language = "en" | "es" | "pt"

const routes = {
  en: "/lp/how-embedded-finance-can-grow-substantially-with-gens-y-and-z/",
  es: "/es/lp/%20las-generaciones-futuras-estan-impulsando-la-demanda/",
  pt: "/pt/lp/como-o-financeiro-embutido-pode-crescer-substancialmente-com-as-geracoes-y-e-z",
} as const

export default function Innovations() {
  const titleWidth = useMedia("100%", "100%", "63.77vw", "90vw")
  const textWidth = useMedia("566px", "34.096vw", "59.863vw", "90vw")
  const innerWidth = useMedia("1144px", "68.916vw", "81.445vw", "89.333vw")
  const titleSize = useMedia(text.h4, text.h4, text.h3, text.h1Mobile)
  const textSize = useMedia(text.bodyR, text.bodyR, text.bodyL, text.bodyR)
  const { t, language } = useI18next()

  const route = routes[language as Language]

  const statsData = useMemo(
    () => [
      {
        stat: t("65%"),
        kicker: t("OFFER DIGITAL B2B METHODS"),
        explainer: t(
          "Portion of companies that offer digital B2B payment methods."
        ),
        tooltip: (
          <UniversalLink to="how-embedded-finance-can-grow-substantially-with-gens-y-and-z">
            <Trans>
              How Embedded Finance Can Grow Substantially with Gens Y and Z
            </Trans>
          </UniversalLink>
        ),
      },
      {
        stat: t("30%"),
        kicker: t("EFFECTIVE PAYMENT SOLUTIONS"),
        explainer: t(
          "Share of businesses that believe their current B2B payment solutions are either “very” or “extremely” effective in solving key friction points."
        ),
        tooltip: t("According to Galileo research."),
      },
    ],
    [t]
  )

  const allStats = statsData.map(stat => {
    return (
      <StatCard key={stat.stat}>
        <Absolute>
          <ToolTip bannerText={stat.tooltip} />
        </Absolute>
        <Row>
          <Stat>
            <Trans>{stat.stat}</Trans>
          </Stat>
          <Kicker>
            <Trans>{stat.kicker}</Trans>
          </Kicker>
        </Row>
        <Text>
          <Trans>{stat.explainer}</Trans>
        </Text>
      </StatCard>
    )
  })

  return (
    <Wrapper>
      <InnerWrapper setWidth={innerWidth}>
        <Content>
          <CalloutWrapper>
            <Callout
              variant="lightmode"
              kicker={{
                text: t("B2B INNOVATION") ?? "",
                gradient: colors.seaFoam500,
                icon: "layersAltSeafoam",
              }}
              title={{
                content: t(
                  "Consumers’ changing preferences are leading to B2B innovation"
                ),
                size: titleSize,
                width: titleWidth,
                color: colors.blue800,
              }}
              text={{
                width: textWidth,
                content: t(
                  "Consumers’ growing adoption of digital tools is also prompting business leaders to reexamine their B2B payment processes and how they can make the jump from manual to virtual methods, though complications linger."
                ),
                color: colors.blue200,
                size: textSize,
              }}
              buttons={[
                {
                  text: t("Download Report") ?? "Download Report",
                  arrow: true,
                  type: "secondary",
                  route,
                },
              ]}
            />
          </CalloutWrapper>
          <StatsWrapper>{allStats}</StatsWrapper>
        </Content>
      </InnerWrapper>
    </Wrapper>
  )
}

const StatCard = styled.div`
  position: relative;
  padding-top: 2.108vw;
  margin-bottom: 0.904vw;
  ${media.fullWidth} {
    padding-top: 35px;
    margin-bottom: 15px;
  }
  ${media.tablet} {
    padding-top: 3.418vw;
    width: 35.938vw;
  }
  ${media.mobile} {
    width: 100%;
    padding-top: 9.333vw;
  }
`

const Absolute = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  ${media.tablet} {
    top: -1.465vw;
    right: -1.465vw;
  }
  ${media.mobile} {
    top: -4vw;
    right: -4vw;
  }
`

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.602vw 0 0.904vw;
  border-top: 1px solid ${colors.blue10};

  ${media.fullWidth} {
    padding: 10px 0 15px;
  }
  ${media.tablet} {
    padding: 0.977vw 0 1.465vw;
  }
  ${media.mobile} {
    padding: 2.667vw 0 4vw;
  }
`

const Stat = styled.h4`
  ${text.h4};
  ${transparentText};
  background-image: ${colors.turqSeaHorz};
`

const Kicker = styled.h6`
  ${text.captionKicker};
  text-align: right;
  width: 8.611vw;
  color: ${colors.blue800};
  ${media.fullWidth} {
    width: 124px;
  }
  ${media.tablet} {
    width: 15.602vw;
  }
  ${media.mobile} {
    width: 34.133vw;
  }
`

const Text = styled.p`
  ${text.bodyR};
  color: ${colors.blue200};
`

const Wrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: 9.036vw 0;
  overflow-x: hidden;

  ${media.fullWidth} {
    padding: 150px 0;
  }
  ${media.tablet} {
    padding: 14.648vw 0;
  }
  ${media.mobile} {
    padding: 21.333vw 0;
  }
`

const Content = styled.div`
  display: flex;
  gap: 7.892vw;
  ${media.fullWidth} {
    gap: 131px;
  }
  ${media.tablet} {
    flex-direction: column;
    gap: 5.859vw;
  }
  ${media.mobile} {
    flex-direction: column;
    gap: 10.667vw;
  }
`

const CalloutWrapper = styled.div`
  width: 38.855vw;
  ${media.fullWidth} {
    width: 645px;
  }
  ${media.tablet} {
    width: fit-content;
  }
  ${media.mobile} {
    width: fit-content;
  }
`

const StatsWrapper = styled.div`
  width: 22.169vw;

  ${media.fullWidth} {
    width: 368px;
    padding: 10px 0 15px;
  }
  ${media.tablet} {
    display: flex;
    justify-content: space-between;
    width: 100%;
  }
  ${media.mobile} {
    width: 100%;
  }
`
